import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Copy, Check, Lock } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2011e831-9e14-4050-89e9-67f554973600/artifacts/1oxcap9f_Logo%20The%20Rig.svg";

const COLORS = [
  { name: "Yellow", hex: "#FFD60A", css: "--rig-yellow", tw: "rig-yellow", usage: "Accents, highlights, CTA hover, OTP codes" },
  { name: "Orange", hex: "#FF6B2B", css: "--rig-orange", tw: "rig-orange", usage: "Primary CTA buttons, active states, links" },
  { name: "Green", hex: "#2DC653", css: "--rig-green", tw: "rig-green", usage: "Success states, confirmations, member badges" },
  { name: "Teal", hex: "#0A6E6E", css: "--rig-teal", tw: "rig-teal", usage: "Avatars, secondary accents, links" },
  { name: "Charcoal", hex: "#1E1E1E", css: "--rig-charcoal", tw: "rig-charcoal", usage: "Primary text, headings, dark backgrounds, buttons" },
  { name: "Off-White", hex: "#FFFDF7", css: "--rig-offwhite", tw: "rig-offwhite", usage: "Page background" },
  { name: "Cream", hex: "#FFF8E7", css: "--rig-cream", tw: "rig-cream", usage: "Warm highlight backgrounds, callout boxes" },
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={copy} className="p-1 rounded hover:bg-black/5 transition-colors" title="Copy">
      {copied ? <Check className="h-3.5 w-3.5 text-rig-green" /> : <Copy className="h-3.5 w-3.5 text-rig-charcoal/30" />}
    </button>
  );
}

function ColorSwatch({ color }) {
  const isLight = ["#FFD60A", "#FFFDF7", "#FFF8E7", "#2DC653"].includes(color.hex);
  return (
    <div className="group" data-testid={`color-${color.name.toLowerCase()}`}>
      <div
        className="h-24 rounded-lg mb-3 flex items-end p-3 transition-transform group-hover:scale-[1.02]"
        style={{ backgroundColor: color.hex }}
      >
        <span className={`text-xs font-mono font-bold ${isLight ? "text-rig-charcoal/70" : "text-white/80"}`}>
          {color.hex}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <h4 className="font-heading font-bold text-sm text-rig-charcoal">{color.name}</h4>
        <CopyButton text={color.hex} />
      </div>
      <p className="text-xs text-rig-charcoal/50 mt-0.5 font-mono">{color.css}</p>
      <p className="text-xs text-rig-charcoal/60 mt-1">{color.usage}</p>
    </div>
  );
}

export default function BrandGuide() {
  const [unlocked, setUnlocked] = useState(false);
  const [code, setCode] = useState("");
  const PASSCODE = "TheRig2026";

  useEffect(() => {
    document.title = "Brand Guide | The Rig";
    if (sessionStorage.getItem("brand_unlocked") === "1") setUnlocked(true);
  }, []);

  const handleUnlock = (e) => {
    e.preventDefault();
    if (code === PASSCODE) {
      setUnlocked(true);
      sessionStorage.setItem("brand_unlocked", "1");
    } else {
      toast.error("Incorrect passcode");
      setCode("");
    }
  };

  if (!unlocked) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4" data-testid="brand-gate">
        <title>Brand Guide | The Rig</title>
        <Card className="w-full max-w-sm border-border rounded-none">
          <CardContent className="pt-8 pb-6 px-6">
            <div className="text-center mb-6">
              <Lock className="h-8 w-8 mx-auto text-rig-charcoal/30 mb-3" />
              <h2 className="font-heading font-bold text-lg text-rig-charcoal">Brand Guide</h2>
              <p className="text-xs text-muted-foreground mt-1">Enter the passcode to access</p>
            </div>
            <form onSubmit={handleUnlock} className="space-y-4">
              <Input
                type="password"
                value={code}
                onChange={e => setCode(e.target.value)}
                placeholder="Passcode"
                autoFocus
                data-testid="brand-passcode-input"
              />
              <button
                type="submit"
                className="w-full bg-rig-charcoal text-white py-2.5 text-sm font-semibold hover:bg-rig-charcoal/90 transition-colors"
                data-testid="brand-unlock-btn"
              >
                Unlock
              </button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="brand-guide-page">
      <title>Brand Guide | The Rig</title>

      {/* Header */}
      <div className="mb-12">
        <Badge className="bg-rig-charcoal text-rig-yellow border-0 mb-3">Internal</Badge>
        <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-rig-charcoal mb-2">
          Brand Guide
        </h1>
        <p className="text-rig-charcoal/60 text-base max-w-2xl">
          Visual identity guidelines for The Rig. Use this reference to maintain consistency across all materials.
        </p>
      </div>

      {/* Logo */}
      <section className="mb-12" data-testid="brand-logo-section">
        <h2 className="text-xl font-heading font-bold text-rig-charcoal mb-4">Logo</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-border rounded-none overflow-hidden">
            <CardContent className="p-8 flex items-center justify-center bg-white min-h-[180px]">
              <img src={LOGO_URL} alt="The Rig — Light" className="h-20" />
            </CardContent>
            <div className="px-4 py-3 bg-rig-charcoal/5 text-xs text-rig-charcoal/60 flex items-center justify-between">
              <span>On light backgrounds</span>
              <CopyButton text={LOGO_URL} />
            </div>
          </Card>
          <Card className="border-border rounded-none overflow-hidden">
            <CardContent className="p-8 flex items-center justify-center bg-rig-charcoal min-h-[180px]">
              <img src={LOGO_URL} alt="The Rig — Dark" className="h-20 brightness-0 invert" />
            </CardContent>
            <div className="px-4 py-3 bg-rig-charcoal/5 text-xs text-rig-charcoal/60">
              On dark backgrounds (invert)
            </div>
          </Card>
        </div>
        <div className="mt-4 bg-rig-cream border border-rig-yellow/30 rounded-lg p-4">
          <h4 className="text-sm font-bold text-rig-charcoal mb-2">Logo Rules</h4>
          <ul className="text-xs text-rig-charcoal/70 space-y-1">
            <li>Minimum clear space: equal to the height of the "R" on all sides</li>
            <li>Do not stretch, rotate, or alter the logo proportions</li>
            <li>Do not place the logo on busy or low-contrast backgrounds</li>
            <li>Use the SVG format whenever possible for sharpness at any size</li>
          </ul>
        </div>
      </section>

      <Separator className="mb-12" />

      {/* Color Palette */}
      <section className="mb-12" data-testid="brand-colors-section">
        <h2 className="text-xl font-heading font-bold text-rig-charcoal mb-2">Color Palette</h2>
        <p className="text-sm text-rig-charcoal/60 mb-6">
          The palette is vibrant and energetic — reflecting the creative, dynamic nature of coworking.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
          {COLORS.map(c => <ColorSwatch key={c.hex} color={c} />)}
        </div>

        {/* Stripe pattern */}
        <div className="mt-8">
          <h3 className="text-sm font-heading font-bold text-rig-charcoal mb-3">Signature Stripe</h3>
          <p className="text-xs text-rig-charcoal/60 mb-3">
            The four-color stripe is used as a brand element on emails, cards, and section dividers.
          </p>
          <div className="h-3 flex rounded-full overflow-hidden">
            <div className="flex-1 bg-[#FFD60A]" />
            <div className="flex-1 bg-[#FF6B2B]" />
            <div className="flex-1 bg-[#2DC653]" />
            <div className="flex-1 bg-[#0A6E6E]" />
          </div>
          <div className="mt-2 flex">
            <span className="flex-1 text-[10px] text-center text-rig-charcoal/40">Yellow</span>
            <span className="flex-1 text-[10px] text-center text-rig-charcoal/40">Orange</span>
            <span className="flex-1 text-[10px] text-center text-rig-charcoal/40">Green</span>
            <span className="flex-1 text-[10px] text-center text-rig-charcoal/40">Teal</span>
          </div>
        </div>
      </section>

      <Separator className="mb-12" />

      {/* Typography */}
      <section className="mb-12" data-testid="brand-typography-section">
        <h2 className="text-xl font-heading font-bold text-rig-charcoal mb-2">Typography</h2>
        <p className="text-sm text-rig-charcoal/60 mb-6">
          Two primary typefaces create contrast between headings and body text.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-border rounded-none">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <Badge variant="outline" className="text-xs">Headings</Badge>
                <CopyButton text="Space Grotesk" />
              </div>
              <p className="font-heading text-4xl font-extrabold text-rig-charcoal leading-tight">
                Space Grotesk
              </p>
              <p className="font-heading text-lg text-rig-charcoal/60 mt-2">
                The quick brown fox jumps over the lazy dog
              </p>
              <div className="mt-4 space-y-1">
                <p className="font-heading font-extrabold text-sm text-rig-charcoal">ExtraBold (800) — Page titles</p>
                <p className="font-heading font-bold text-sm text-rig-charcoal/80">Bold (700) — Section headings</p>
                <p className="font-heading font-semibold text-sm text-rig-charcoal/60">SemiBold (600) — Card titles</p>
                <p className="font-heading font-medium text-sm text-rig-charcoal/40">Medium (500) — Subtitles</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border rounded-none">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <Badge variant="outline" className="text-xs">Body</Badge>
                <CopyButton text="Inter" />
              </div>
              <p className="text-4xl font-bold text-rig-charcoal leading-tight">
                Inter
              </p>
              <p className="text-lg text-rig-charcoal/60 mt-2">
                The quick brown fox jumps over the lazy dog
              </p>
              <div className="mt-4 space-y-1">
                <p className="font-bold text-sm text-rig-charcoal">Bold (700) — Emphasis, labels</p>
                <p className="font-semibold text-sm text-rig-charcoal/80">SemiBold (600) — Buttons, nav</p>
                <p className="font-medium text-sm text-rig-charcoal/60">Medium (500) — Body text</p>
                <p className="font-normal text-sm text-rig-charcoal/40">Regular (400) — Descriptions</p>
              </div>
            </CardContent>
          </Card>
        </div>
        <Card className="border-border rounded-none mt-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Badge variant="outline" className="text-xs">Monospace</Badge>
              <CopyButton text="JetBrains Mono" />
            </div>
            <p className="font-mono text-2xl font-medium text-rig-charcoal">JetBrains Mono</p>
            <p className="font-mono text-sm text-rig-charcoal/60 mt-2">
              Used for codes, references, OTP digits, and technical data.
            </p>
            <p className="font-mono text-sm text-rig-charcoal/40 mt-1">ABC123 — booking-ref — XCG 499.00</p>
          </CardContent>
        </Card>

        <div className="mt-6 bg-rig-charcoal/5 rounded-lg p-4">
          <h4 className="text-sm font-bold text-rig-charcoal mb-2">Type Scale</h4>
          <div className="space-y-2">
            {[
              { label: "H1 — Page Title", cls: "text-4xl sm:text-5xl font-heading font-extrabold" },
              { label: "H2 — Section", cls: "text-xl font-heading font-bold" },
              { label: "H3 — Card Title", cls: "text-base font-heading font-semibold" },
              { label: "Body", cls: "text-sm" },
              { label: "Small / Caption", cls: "text-xs" },
            ].map(t => (
              <div key={t.label} className="flex items-baseline gap-4">
                <span className="text-[10px] font-mono text-rig-charcoal/30 w-28 flex-shrink-0">{t.label}</span>
                <span className={`${t.cls} text-rig-charcoal`}>The Rig Coworking</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Separator className="mb-12" />

      {/* UI Elements */}
      <section className="mb-12" data-testid="brand-elements-section">
        <h2 className="text-xl font-heading font-bold text-rig-charcoal mb-2">UI Elements</h2>
        <p className="text-sm text-rig-charcoal/60 mb-6">
          Key design patterns used throughout the platform.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Buttons */}
          <Card className="border-border rounded-none">
            <CardContent className="p-6">
              <h3 className="text-sm font-heading font-bold text-rig-charcoal mb-4">Buttons</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <button className="bg-rig-charcoal text-white px-5 py-2 text-sm font-semibold">Primary</button>
                  <span className="text-xs text-rig-charcoal/40">rounded-none, bg-foreground</span>
                </div>
                <div className="flex items-center gap-3">
                  <button className="bg-rig-orange text-white px-5 py-2 rounded-full text-sm font-bold">CTA</button>
                  <span className="text-xs text-rig-charcoal/40">rounded-full, bg-rig-orange</span>
                </div>
                <div className="flex items-center gap-3">
                  <button className="border border-rig-charcoal/20 px-5 py-2 text-sm font-medium text-rig-charcoal">Outline</button>
                  <span className="text-xs text-rig-charcoal/40">border, transparent bg</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cards */}
          <Card className="border-border rounded-none">
            <CardContent className="p-6">
              <h3 className="text-sm font-heading font-bold text-rig-charcoal mb-4">Cards</h3>
              <div className="space-y-3">
                <div className="border border-border p-4">
                  <p className="font-heading font-bold text-sm text-rig-charcoal">Sharp corners</p>
                  <p className="text-xs text-rig-charcoal/50 mt-1">rounded-none, border-border</p>
                </div>
                <p className="text-xs text-rig-charcoal/60">
                  All cards use sharp corners (no border-radius) for an industrial, modern feel that matches the "Rig" concept.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Badges */}
          <Card className="border-border rounded-none">
            <CardContent className="p-6">
              <h3 className="text-sm font-heading font-bold text-rig-charcoal mb-4">Badges & Status</h3>
              <div className="flex flex-wrap gap-2">
                <Badge className="bg-red-100 text-red-800 border-0">Admin</Badge>
                <Badge className="bg-blue-100 text-blue-800 border-0">Manager</Badge>
                <Badge className="bg-green-100 text-green-800 border-0">Member</Badge>
                <Badge className="bg-gray-100 text-gray-800 border-0">Guest</Badge>
                <Badge className="bg-green-100 text-green-800 border-0">Confirmed</Badge>
                <Badge className="bg-amber-100 text-amber-800 border-0">Pending</Badge>
                <Badge className="bg-red-100 text-red-800 border-0">Cancelled</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Avatar */}
          <Card className="border-border rounded-none">
            <CardContent className="p-6">
              <h3 className="text-sm font-heading font-bold text-rig-charcoal mb-4">Avatars</h3>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-rig-teal text-white flex items-center justify-center text-sm font-bold">J</div>
                <div className="h-8 w-8 rounded-full bg-rig-teal text-white flex items-center justify-center text-xs font-bold">A</div>
                <div className="h-7 w-7 rounded-full bg-rig-teal text-white flex items-center justify-center text-xs font-bold">M</div>
              </div>
              <p className="text-xs text-rig-charcoal/60 mt-3">
                Round, teal background, white initial. Used in navbar and profile cards.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <Separator className="mb-12" />

      {/* Currency & Copy */}
      <section className="mb-12" data-testid="brand-voice-section">
        <h2 className="text-xl font-heading font-bold text-rig-charcoal mb-2">Voice & Copy</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-border rounded-none">
            <CardContent className="p-6">
              <h3 className="text-sm font-heading font-bold text-rig-charcoal mb-3">Currency</h3>
              <p className="font-heading font-extrabold text-2xl text-rig-charcoal">XCG</p>
              <p className="text-xs text-rig-charcoal/60 mt-2">
                Always prefix amounts with "XCG" and use two decimal places.
              </p>
              <div className="mt-3 bg-rig-charcoal/5 rounded p-3 font-mono text-sm text-rig-charcoal">
                XCG 299.00 &nbsp;|&nbsp; XCG 1,499.00
              </div>
            </CardContent>
          </Card>
          <Card className="border-border rounded-none">
            <CardContent className="p-6">
              <h3 className="text-sm font-heading font-bold text-rig-charcoal mb-3">Tone</h3>
              <ul className="text-sm text-rig-charcoal/70 space-y-2">
                <li><strong className="text-rig-charcoal">Professional</strong> — clear, concise, no jargon</li>
                <li><strong className="text-rig-charcoal">Warm</strong> — welcoming but not overly casual</li>
                <li><strong className="text-rig-charcoal">Action-oriented</strong> — "Book Now", "Get Started"</li>
                <li><strong className="text-rig-charcoal">Emmastad-proud</strong> — emphasize central location</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      <Separator className="mb-12" />

      {/* Email Template */}
      <section className="mb-12" data-testid="brand-email-section">
        <h2 className="text-xl font-heading font-bold text-rig-charcoal mb-2">Email Template</h2>
        <p className="text-sm text-rig-charcoal/60 mb-4">All transactional emails follow this structure:</p>
        <Card className="border-border rounded-none overflow-hidden max-w-md mx-auto">
          <div className="h-1 flex">
            <div className="flex-1 bg-[#FFD60A]" />
            <div className="flex-1 bg-[#FF6B2B]" />
            <div className="flex-1 bg-[#2DC653]" />
            <div className="flex-1 bg-[#0A6E6E]" />
          </div>
          <div className="bg-rig-charcoal p-5 text-center">
            <p className="font-heading text-xl font-bold text-white">The Rig</p>
            <p className="text-white/40 text-xs mt-0.5">Coworking Space</p>
          </div>
          <CardContent className="p-6">
            <p className="font-heading font-bold text-base text-rig-charcoal">Email Heading</p>
            <p className="text-sm text-rig-charcoal/70 mt-2">Body content goes here with clear, actionable information.</p>
            <div className="bg-rig-charcoal/5 rounded-lg p-3 mt-3 text-xs text-rig-charcoal/50">
              Data table or callout box
            </div>
          </CardContent>
          <div className="bg-rig-charcoal p-3 text-center">
            <p className="text-white/30 text-[10px]">The Rig Coworking Space | therig@ic-foundation.org</p>
          </div>
        </Card>
      </section>

      <Separator className="mb-12" />

      {/* Quick Reference */}
      <section data-testid="brand-reference-section">
        <h2 className="text-xl font-heading font-bold text-rig-charcoal mb-4">Quick Reference</h2>
        <Card className="border-border rounded-none">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              {[
                ["Heading Font", "Space Grotesk"],
                ["Body Font", "Inter"],
                ["Mono Font", "JetBrains Mono"],
                ["Primary BG", "#FFFDF7 (Off-White)"],
                ["Primary Text", "#1E1E1E (Charcoal)"],
                ["CTA Color", "#FF6B2B (Orange)"],
                ["Card Style", "rounded-none, border"],
                ["Button Style", "rounded-none (primary), rounded-full (CTA)"],
                ["Currency", "XCG (2 decimals)"],
                ["Location", "Emmastad, Willemstad"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-baseline gap-2">
                  <span className="text-rig-charcoal/40 text-xs w-28 flex-shrink-0">{k}</span>
                  <span className="text-rig-charcoal font-medium">{v}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>

      <div className="mt-10 text-center text-xs text-rig-charcoal/30">
        <p>The Rig Brand Guide — For internal use</p>
      </div>
    </div>
  );
}
